import { useCallback, useEffect, useState, VoidFunctionComponent } from 'react';
import { useThree } from '@react-three/fiber';
import { getGeometrySize } from '@/lib/geometry';
import { Image } from '@/components/Image';
import { useResponsive } from '@/hooks/useResponsive';
import { BotIcon } from '@/components/BotIcon';
import connectControllerImg from '@/img/connect-controller.png';
import { deviceProperties } from '@/lib/deviceProperties';
import { OrientationPermissionState } from '@/lib/types';

const CONNECT_ICON_ASPECT = 197 / 180;
const CONNECT_ICON_SCALAR = 0.3;

interface Props {
    onRequested: () => void;
    requestOrientation: () => Promise<boolean>;
    orientationPermission: OrientationPermissionState | undefined;
}
export const RequestOrientation: VoidFunctionComponent<Props> = ({
    onRequested,
    requestOrientation,
    orientationPermission,
}) => {
    const { supportsOrientation } = deviceProperties();
    const [connectIconSize, setConnectIconSize] = useState<[number, number]>([0, 0]);
    const { viewport } = useThree();
    const { responsiveValue } = useResponsive();
    const iconSizeFactor = responsiveValue(0.8, 0.95); // take up 90% of available space
    const [animationComplete, setAnimationComplete] = useState<boolean>(false);

    useEffect(() => {
        // Set the size of the onlybots icon based on the window size, trying
        // to fill whichever dimension is smallest
        setConnectIconSize(getGeometrySize(viewport.width, viewport.height, CONNECT_ICON_ASPECT));
    }, [viewport, iconSizeFactor]);

    const handleClick = useCallback(async () => {
        if (supportsOrientation && orientationPermission !== 'denied') {
            await requestOrientation();
        }
        onRequested();
    }, [onRequested, orientationPermission, requestOrientation, supportsOrientation]);

    return (
        <BotIcon
            initialOpacity={1}
            initialScale={0.1775}
            onAnimationComplete={() => setAnimationComplete(true)}
            onClick={() => void handleClick()}
        >
            {animationComplete && (
                <Image
                    url={connectControllerImg}
                    scale={[connectIconSize[0] * CONNECT_ICON_SCALAR, connectIconSize[1] * CONNECT_ICON_SCALAR, 0]}
                    position={[0, -0.1, 2]}
                    transparent
                />
            )}
        </BotIcon>
    );
};
